import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Wrench, MapPin, ArrowLeft, Star } from "lucide-react";
import localApi from "@/lib/localApi";
import { fmtDate } from "@/lib/api";
import { StarsDisplay } from "@/components/StarInput";
import { Button } from "@/components/ui/button";

const CAT_LABELS = { quality: "Quality of Work", communication: "Communication", punctuality: "Punctuality" };

export default function MechanicProfile() {
  const { email } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    localApi.get(`/marketplace/mechanics/${encodeURIComponent(email)}/public`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [email]);

  if (loading) return <div className="min-h-screen bg-slate-950 flex justify-center pt-32"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  if (notFound) return <div className="min-h-screen bg-slate-950 text-slate-400 flex flex-col items-center justify-center gap-4"><p>Mechanic profile not found.</p><Button onClick={() => navigate(-1)} className="bg-amber-500 text-slate-950">Go back</Button></div>;

  const rating = data.rating || {};
  const subs = rating.subs || {};

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <Button variant="ghost" data-testid="profile-back" onClick={() => navigate(-1)} className="text-slate-400 hover:text-white mb-6"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back</Button>

        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid="mechanic-profile-card">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-xl bg-amber-500/15 flex items-center justify-center"><Wrench className="h-7 w-7 text-amber-400" /></div>
            <div>
              <h1 className="font-head font-black text-2xl text-slate-50" data-testid="profile-business-name">{data.business_name || data.name || "Pitcrew Mechanic"}</h1>
              <p className="text-sm text-slate-400 flex items-center gap-1.5 mt-0.5"><MapPin className="h-3.5 w-3.5" /> {data.place_name || ""} {data.service_zip}</p>
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3" data-testid="profile-overall-rating">
            {rating.avg ? (
              <>
                <span className="font-head font-black text-4xl text-amber-400">{rating.avg.toFixed(1)}</span>
                <div>
                  <StarsDisplay value={rating.avg} size="h-4 w-4" />
                  <p className="text-xs text-slate-500 mt-1">{rating.count} review{rating.count === 1 ? "" : "s"}</p>
                </div>
              </>
            ) : (
              <p className="text-slate-500" data-testid="profile-no-rating">No reviews yet — be the first to work with them.</p>
            )}
          </div>

          {rating.avg && (
            <div className="mt-6 space-y-2" data-testid="profile-subcategories">
              {Object.keys(CAT_LABELS).map((k) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">{CAT_LABELS[k]}</span>
                  <StarsDisplay value={subs[k]} size="h-3.5 w-3.5" showNumber />
                </div>
              ))}
            </div>
          )}
        </div>

        {data.reviews?.length > 0 && (
          <div className="mt-6 space-y-3" data-testid="profile-reviews">
            <h2 className="font-head font-bold text-lg text-slate-100">Recent reviews</h2>
            {data.reviews.map((rv, i) => (
              <div key={rv.created_at || i} data-testid={`profile-review-${i}`} className="rounded-xl border border-slate-800 bg-slate-900 p-4">
                <div className="flex items-center justify-between">
                  <StarsDisplay value={rv.overall} size="h-4 w-4" showNumber />
                  <span className="text-[11px] text-slate-600">{fmtDate(rv.created_at)}</span>
                </div>
                {rv.vehicle && <p className="text-xs text-slate-500 mt-1">{rv.vehicle}</p>}
                {rv.comment && <p className="text-sm text-slate-300 mt-2 flex items-start gap-2"><Star className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" /> {rv.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
